import type { NotificationItemComponent } from "./NotificationItem";
import React from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Typography, Button } from "@mui/material";
import NotificationItem from "./NotificationItem";
import { useSession } from "@/security/RouteGuards";
import { useNavigate } from "@tanstack/react-router";
import useRecommended from "@/components/subscription/customize/hooks/useRecommended";
import useCustomizeQuery from "@/components/newcustomize/hooks/useCustomizeQuery";
import usePlan from "@/hooks/usePlan";

const HundredPercentBandwidthUsedNotification: NotificationItemComponent = ({
  item,
}) => {
  const navigate = useNavigate();
  const { bandwidth } = useRecommended({ planId: item.context?.plan });
  const { data: plan } = usePlan(item.context?.plan || "default");
  const { subscription } = useSession();
  const { link: customizeHref } = useCustomizeQuery({
    source: "notification",
    showCustomizeSections: ["bandwidth-plan", "plan-terms"],
    existingPlanId: item.context?.plan,
    ...bandwidth,
  });

  const computedMessage = React.useMemo((): string => {
    if (!plan && !subscription)
      return "You have reached the proxy limit in your current plan.";

    const term = subscription?.term === "monthly" ? "month" : "year";
    const bandwidth = plan?.bandwidth_limit ?? 100;

    return [
      "You have reached the proxy limit of",
      `${bandwidth}GB/${term}`,
      "in your plan. ",
    ].join(" ");
  }, [plan, subscription]);

  const handleUpgrade = React.useCallback(() => {
    void navigate({ ...customizeHref, replace: true });
  }, [navigate, customizeHref]);

  return (
    <NotificationItem variant={!item.dismissed_at ? "error" : "default"}>
      <NotificationItem.Title created_at={item.created_at}>
        <span>Your proxy activity is suspended</span>
      </NotificationItem.Title>
      <NotificationItem.Content>
        <Typography variant="body2">
          <span>{computedMessage}</span>
          <span>
            Your proxy activity is currently suspended. Upgrade to get more
            bandwidth.
          </span>
        </Typography>
      </NotificationItem.Content>
      <NotificationItem.Actions {...item}>
        <Button
          size="small"
          variant={!item.dismissed_at ? "contained" : "outlined"}
          color={!item.dismissed_at ? "error" : "primary"}
          disableElevation
          onClick={handleUpgrade}
          endIcon={<OpenInNewIcon fontSize="small" />}
        >
          <span>Upgrade</span>
        </Button>
      </NotificationItem.Actions>
    </NotificationItem>
  );
};

export default HundredPercentBandwidthUsedNotification;
